import { useState } from 'react'
import { find } from 'lodash'
import { useDispatch } from 'react-redux'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { WorkspacePortalService } from 'api/services/WorkspacePortalService'
import { WorkspaceSettings } from 'types'
import { workspacesSet } from 'redux/actions'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from './SecuritySettings.module.scss'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { VcmToggleButton } from './VcmToggleButton'
import { useModals } from 'almanac-ui/Modal'
import { ConfirmModal } from './ConfirmModal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'

interface Props {
  workspace: WorkspaceSettings
}

export const WorkspaceAdminForm = ({ workspace }: Props) => {
  const [isSaving, setIsSaving] = useState(false)
  const [allowPublicLinks, setAllowPublicLinks] = useState(!!workspace.allow_public_links)
  const [versionControlMode, setVersionControlMode] = useState(workspace?.version_control_mode.value)
  const { openModal } = useModals()
  const versionControlModeFeature = useFlipperFlag('version_control_mode')

  const dispatch = useDispatch()
  const workspaces = useWorkspaces()

  const handleSettingsChange = async (settings: { [key: string]: any }) => {
    if (isSaving || !workspace) return
    setIsSaving(true)

    const res = await client.updateWorkspaceSettings(settings, workspace.key)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      const { version_control_mode, allow_public_links } = res.data.company
      setAllowPublicLinks(allow_public_links)
      setVersionControlMode(version_control_mode.value)

      if (settings.workspace_style?.show_portal) {
        WorkspacePortalService.getPortal()
      } else {
        WorkspacePortalService.portalReset()
      }

      // NOTE: update the workspace in state for the new nav
      const stateWorkspace = find(workspaces, { id: workspace.id })
      if (stateWorkspace) {
        dispatch(
          workspacesSet(
            workspaces.map((aWorkspace) =>
              aWorkspace.id === stateWorkspace.id
                ? {
                    ...aWorkspace,
                    version_control_mode: res.data.company.version_control_mode,
                    version_lock: res.data.company.version_lock,
                    allow_public_links: res.data.company.allow_public_links,
                    show_make_public:
                      settings.workspace_style?.show_make_public !== undefined
                        ? !!settings.workspace_style?.show_make_public
                        : aWorkspace.show_make_public,
                  }
                : aWorkspace,
            ),
          ),
        )
      }

      showToastMessage('Your workspace has been updated!', 'success')
    } else {
      setVersionControlMode(workspace.version_control_mode.value)
      showToastMessage("Couldn't update your workspace settings, please try again later", 'error')
    }
    setIsSaving(false)
  }

  return (
    <div className={Styles.settingsContent}>
      <div className={Styles.settingsOptions}>
        {versionControlModeFeature && (
          <div className={Styles.settingsOption}>
            <div className={Styles.toggleRow}>
              <Heading level={5}>Version Control Mode</Heading>
              <VcmToggleButton
                onToggle={setVersionControlMode}
                onChange={(isChecked) => {
                  setVersionControlMode(isChecked)
                  handleSettingsChange({ workspace: { version_control_mode: isChecked } })
                }}
                isChecked={versionControlMode}
              />
            </div>
            <div className={Styles.helpText}>
              <Text variant="secondary" color="var(--dark-60)">
                Edits to all docs in this workspace must be merged in using layers.
              </Text>
            </div>
          </div>
        )}

        <div className={Styles.settingsOption}>
          <div className={Styles.toggleRow}>
            <Heading level={5}>Disable Public link sharing</Heading>
            <ToggleButton
              name="allowPublicLinks"
              checked={!allowPublicLinks}
              handleChange={() => {
                if (allowPublicLinks) {
                  openModal(
                    ConfirmModal,
                    {
                      handleSettingsChange: () => handleSettingsChange({ workspace: { allow_public_links: false } }),
                    },
                    { newStack: true },
                  )
                  return
                }
                handleSettingsChange({ workspace: { allow_public_links: true } })
              }}
            />
          </div>
          <div className={Styles.helpText}>
            <Text variant="secondary" color="var(--dark-60)">
              All files and folders in your Workspace will no longer be able to shared publicly via link sharing. Any
              files and folders currently shared publicly will no longer have public access links.
            </Text>
          </div>
        </div>
      </div>
    </div>
  )
}
